import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Eye, Ban, CheckCircle, Package, ShoppingCart, Users, DollarSign, ExternalLink, X, UserCog } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const impersonate = async (tenantId) => {
    try {
      const res = await api.post(`/admin/tenants/${tenantId}/impersonate`);
      const adminToken = localStorage.getItem('token');
      const adminUser = localStorage.getItem('user');
      localStorage.setItem('admin_token', adminToken);
      localStorage.setItem('admin_user', adminUser);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success(`Masuk sebagai ${res.data.tenant.name}`);
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal impersonate tenant');
    }
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/tenants', { params: { search, status } });
      setTenants(res.data.tenants); setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchTenants(); }, [search, status]);

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/tenants/${id}`);
      setSelected(res.data);
    } catch {} finally { setDetailLoading(false); }
  };

  const toggleStatus = async (id, current) => {
    const newStatus = current === 'suspended' ? 'active' : 'suspended';
    await api.put(`/admin/tenants/${id}`, { subscription_status: newStatus, is_active: newStatus !== 'suspended' });
    fetchTenants();
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (s === 'trial') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (s === 'suspended') return 'bg-red-100 text-red-700 border-red-200';
    if (s === 'expired') return 'bg-slate-100 text-slate-700 border-slate-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  return (
    <div data-testid="admin-tenants-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kelola Tenant</h1>
        <span className="text-sm text-slate-500">{total} tenant</span>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari tenant..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="tenant-search-input" />
            </div>
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Semua Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tenants-table">
              <thead><tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Bisnis</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Paket</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Produk</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Order</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Revenue</th>
                <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
              </tr></thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4">
                      <p className="font-medium text-slate-900">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.subdomain}.tokomoe.com</p>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{t.package_name || <span className="text-slate-400">Free</span>}</td>
                    <td className="py-3 px-4 text-slate-600">{t.product_count}</td>
                    <td className="py-3 px-4 text-slate-600">{t.order_count}</td>
                    <td className="py-3 px-4 font-medium text-slate-900">Rp {fmt(t.revenue)}</td>
                    <td className="py-3 px-4"><Badge className={statusColor(t.subscription_status)}>{t.subscription_status}</Badge></td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => fetchDetail(t.id)} data-testid={`view-tenant-${t.id}`}><Eye size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => window.open(`/store/${t.subdomain}`, '_blank')}><ExternalLink size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => toggleStatus(t.id, t.subscription_status)} data-testid={`toggle-tenant-${t.id}`}>
                          {t.subscription_status === 'suspended' ? <CheckCircle size={16} className="text-emerald-500" /> : <Ban size={16} className="text-red-500" />}
                        </Button>
                        <Button variant="ghost" size="icon" title="Masuk sebagai tenant" onClick={() => impersonate(t.id)}>
                          <UserCog size={16} className="text-violet-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!tenants.length && <tr><td colSpan={7} className="py-12 text-center text-slate-400">Belum ada tenant</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2D6CDF]/10 flex items-center justify-center">
                <Package size={20} className="text-[#2D6CDF]" />
              </div>
              {selected?.name}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>
          ) : selected && (
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="orders">Pesanan</TabsTrigger>
                <TabsTrigger value="subscriptions">Subscription</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Package size={18} className="text-[#2D6CDF]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{selected.product_count}</p>
                        <p className="text-xs text-slate-500">Produk</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <ShoppingCart size={18} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{selected.order_count}</p>
                        <p className="text-xs text-slate-500">Pesanan</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                        <Users size={18} className="text-violet-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{selected.customer_count}</p>
                        <p className="text-xs text-slate-500">Pelanggan</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                        <DollarSign size={18} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold">Rp {fmt(selected.total_revenue)}</p>
                        <p className="text-xs text-slate-500">Revenue</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Subdomain</span>
                    <span className="font-medium">{selected.subdomain}.tokomoe.com</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Custom Domain</span>
                    <span className="font-medium">{selected.custom_domain || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Template</span>
                    <span className="font-medium capitalize">{selected.template}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Paket</span>
                    <span className="font-medium">{selected.package?.name || 'Free'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Status</span>
                    <Badge className={statusColor(selected.subscription_status)}>{selected.subscription_status}</Badge>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">Owner</span>
                    <span className="font-medium">{selected.owner?.name} ({selected.owner?.email})</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-500">Dibuat</span>
                    <span className="font-medium">{new Date(selected.created_at).toLocaleString('id-ID')}</span>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="orders" className="mt-4">
                <div className="space-y-2">
                  {selected.recent_orders?.map((o, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{o.order_number}</p>
                        <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString('id-ID')}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">Rp {fmt(o.total)}</p>
                        <Badge variant="outline" className={o.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{o.payment_status}</Badge>
                      </div>
                    </div>
                  ))}
                  {!selected.recent_orders?.length && <p className="text-center text-slate-400 py-8">Belum ada pesanan</p>}
                </div>
              </TabsContent>
              <TabsContent value="subscriptions" className="mt-4">
                <div className="space-y-2">
                  {selected.subscriptions?.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{s.package_name}</p>
                        <p className="text-xs text-slate-500">{s.billing_cycle} • {new Date(s.created_at).toLocaleDateString('id-ID')}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">Rp {fmt(s.amount)}</p>
                        <Badge variant="outline" className={statusColor(s.status)}>{s.status}</Badge>
                      </div>
                    </div>
                  ))}
                  {!selected.subscriptions?.length && <p className="text-center text-slate-400 py-8">Belum ada subscription</p>}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
